import { A11yModule } from '@angular/cdk/a11y';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ButtonDirective } from './button.directive';

/**
 * Data contract for the shared confirm dialog. `confirmLabel`/`cancelLabel` are plain strings so
 * each app passes its OWN translated text (keeps shared-ui i18n-agnostic — no Transloco keys that
 * would have to exist in every app's i18n files). `variant: 'destructive'` reddens the confirm
 * button for delete/irreversible actions.
 */
export interface ConfirmationDialogData {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
}

/**
 * Canonical confirm dialog (CDK Dialog + libBtn) — the single target for every confirm/irreversible
 * prompt across both apps. Shares the visual language of `lib-alert`: a variant-tinted icon, a
 * title + description, and Cancel / Confirm actions. Returns `true`/`false` via `DialogRef.close`.
 */
@Component({
  selector: 'lib-confirmation-dialog',
  standalone: true,
  imports: [A11yModule, ButtonDirective],
  template: `
    <div
      cdkTrapFocus
      class="w-[26rem] max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-800">
      <div class="flex items-start gap-3.5">
        <span class="flex size-10 shrink-0 items-center justify-center rounded-full" [class]="iconWrapClass">
          @if (dialogData.variant === 'destructive') {
            <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          } @else {
            <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
            </svg>
          }
        </span>
        <div class="min-w-0 flex-1 pt-0.5">
          <h3 class="text-base font-semibold text-gray-900 dark:text-slate-100">{{ dialogData.title }}</h3>
          @if (dialogData.description; as description) {
            <p class="mt-1 text-sm leading-relaxed text-gray-500 dark:text-slate-400">{{ description }}</p>
          }
        </div>
      </div>

      <div class="mt-5 flex justify-end gap-2">
        <button libBtn variant="outline" (click)="dialogRef.close(false)">
          {{ dialogData.cancelLabel ?? 'Cancel' }}
        </button>
        <button
          libBtn
          [variant]="dialogData.variant === 'destructive' ? 'destructive' : 'default'"
          cdkFocusInitial
          (click)="dialogRef.close(true)">
          {{ dialogData.confirmLabel ?? 'Confirm' }}
        </button>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationDialogComponent {
  dialogData: ConfirmationDialogData = inject(DIALOG_DATA);
  dialogRef = inject<DialogRef<boolean>>(DialogRef);

  protected get iconWrapClass(): string {
    return this.dialogData.variant === 'destructive'
      ? 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400'
      : 'bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400';
  }

  constructor() {
    if (!this.dialogData?.title) throw Error('ConfirmationDialogComponent has no title!');
  }
}
